import {
  BooleanFilterValue,
  CheckboxFilter,
  FilterType,
  FilterValue,
  InputType,
} from "../../../shared/filters/types/filter";
import type { ApplyFilter } from "../../../shared/filters/types/apply";
import type { Launch } from "../../types/launch";
import {
  sortedUnique,
  toFiscalYearLabel,
  type LaunchMediaRow,
} from "../../utils/launchMediaFilters";

const FISCAL_YEAR_PREFIX = "fiscalYear-";
const FISCAL_YEAR_ANY_ID = "fiscalYearAny";

export function useFiscalYearCheckboxFilters(
  launches: Launch[]
): CheckboxFilter[] {
  const years = sortedUnique(
    launches
      .map((l) => l.launch_year)
      .filter((y): y is number => typeof y === "number")
  );
  return years.map((year) => ({
    inputType: InputType.Checkbox,
    filterType: FilterType.Filter,
    id: `${FISCAL_YEAR_PREFIX}${year}`,
    title: toFiscalYearLabel(year),
    enabled: true,
    defaultValue: {
      filterId: `${FISCAL_YEAR_PREFIX}${year}`,
      value: false,
    } as BooleanFilterValue,
  }));
}

/**
 * The framework ANDs all filters, but ticked fiscal years must be ORed. This
 * single non-UI filter reads all ticked year checkboxes.
 */
export function createFiscalYearAnyFilter(
  yearFilters: CheckboxFilter[],
  filterValues: FilterValue[]
): ApplyFilter<CheckboxFilter, LaunchMediaRow> {
  const available = yearFilters.map((f) => f.id);
  const ticked = filterValues
    .filter(
      (v) =>
        v.filterId.startsWith(FISCAL_YEAR_PREFIX) &&
        available.includes(v.filterId) &&
        v.value === true
    )
    .map((v) => Number(v.filterId.slice(FISCAL_YEAR_PREFIX.length)));

  return {
    filter: {
      ...yearFilters[0],
      inputType: InputType.Checkbox,
      filterType: FilterType.Filter,
      id: FISCAL_YEAR_ANY_ID,
      title: FISCAL_YEAR_ANY_ID,
      defaultValue: { filterId: FISCAL_YEAR_ANY_ID, value: false },
    } as CheckboxFilter,
    filterCompare: ({ launch }) =>
      ticked.length === 0 ||
      (launch.launch_year !== undefined && ticked.includes(launch.launch_year)),
  };
}
